import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IntlProvider } from "react-intl";
import itMessages from "./i18n/it_IT.json";
import enMessages from "./i18n/en_US.json";
import frMessages from "./i18n/fr_FR.json";
import deMessages from "./i18n/de_DE.json";
import esMessages from "./i18n/es_ES.json";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
var availableLanguages = ["it", "en", "fr", "de", "es"];
var messagesMap = {
  "it": itMessages,
  "en": enMessages,
  "fr": frMessages,
  "de": deMessages,
  "es": esMessages
};
var lsLanguageMapping = {
  "it": "ITA",
  "en": "ENU",
  "fr": "FRA",
  "de": "DEU",
  "es": "ESP"
};
export function getProductSuffix() {
  return ".html";
}
export function getCategorySuffix() {
  return ".html";
}
var LocaleContext = createContext(null);
export function useLocale() {
  var ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
var LocaleProvider = _ref => {
  var {
    initialLocale,
    children
  } = _ref;
  var browserLocale = typeof navigator !== "undefined" ? navigator.language : "it-IT";
  var storage = new BrowserPersistence();
  var browserLanguageCode = useMemo(() => browserLocale.split("-")[0], [browserLocale]);
  var websiteLanguage = availableLanguages.includes(browserLanguageCode) ? browserLanguageCode : "en";
  var [language, setLocalLanguage] = useState(websiteLanguage);
  var [lsLanguage, setLsLanguage] = useState(lsLanguageMapping[websiteLanguage] || "ITA");
  var [missingTranslationList, setMissingTranslationList] = useState([]);
  var messages = useMemo(() => {
    return messagesMap[language] || itMessages;
  }, [language]);
  var onIntlError = error => {
    if (error.code === "MISSING_TRANSLATION") {
      if (!missingTranslationList.includes(error.descriptor.id)) {
        var _exponea;
        console.warn("Missing translation", error);
        setMissingTranslationList(prevList => [...prevList, error.descriptor.id]);
        if ((_exponea = exponea) !== null && _exponea !== void 0 && _exponea.track) {
          exponea.track("missing_translation", {
            message_id: error.descriptor.id,
            language
          });
        }
      }
      return;
    }
    throw error;
  };
  useEffect(() => {
    storage.setItem("language_code", websiteLanguage);
  }, []);
  var setLanguage = lang => {
    setLocalLanguage(lang);
    storage.setItem("language_code", lang);
    setLsLanguage(lsLanguageMapping[lang] || "ITA");
  };
  var value = useMemo(() => ({
    language,
    setLanguage,
    availableLanguages,
    lsLanguage
  }), [language]);
  return /*#__PURE__*/React.createElement(LocaleContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(IntlProvider, {
    key: language // WHY: force remount when locale changes
    ,
    locale: language,
    defaultLocale: "it-IT",
    messages: messages,
    onError: onIntlError
  }, children));
};
export function getLanguage() {
  var language = navigator.languages.includes("it-IT") || navigator.languages.includes("it") ? "IT" : "EN";
  return language;
}
export default LocaleProvider;